import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { MapPin, Star, Coffee, Search } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Directory = () => {
  const [shops, setShops] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchShops();
  }, []);

  const fetchShops = async () => {
    try {
      const response = await axios.get(`${API}/shops`);
      setShops(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching shops:', error);
      setLoading(false);
    }
  };

  const filteredShops = shops.filter(shop =>
    shop.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    shop.address.toLowerCase().includes(searchQuery.toLowerCase()) ||
    shop.specialties.some(s => s.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  return (
    <div className="min-h-screen grain-overlay">
      <div className="px-6 md:px-12 lg:px-24 py-12">
        <div className="mb-12">
          <h1 className="text-5xl md:text-6xl font-semibold tracking-tight mb-4" data-testid="directory-title">
            Coffee Shop Directory
          </h1>
          <p className="text-lg text-muted-foreground mb-8">
            Browse all {shops.length} coffee shops in your area
          </p>
          
          <div className="max-w-2xl">
            <div className="relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" strokeWidth={1.5} />
              <Input
                data-testid="directory-search-input"
                type="text"
                placeholder="Search by name, location, or specialty..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full h-12 pl-12 pr-4 bg-card border-border"
              />
            </div>
          </div>
        </div>

        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <div key={i} className="animate-pulse">
                <div className="bg-muted/50 h-64 rounded-xl mb-4" />
                <div className="bg-muted/50 h-6 w-3/4 rounded mb-2" />
                <div className="bg-muted/50 h-4 w-1/2 rounded" />
              </div>
            ))}
          </div>
        ) : filteredShops.length === 0 ? (
          <div className="text-center py-20">
            <Coffee className="h-16 w-16 text-muted-foreground mx-auto mb-4" strokeWidth={1.5} />
            <h3 className="text-2xl font-medium mb-2">
              {searchQuery ? 'No results found' : 'No coffee shops yet'}
            </h3>
            <p className="text-muted-foreground">
              {searchQuery
                ? 'Try adjusting your search terms'
                : 'Check back soon for local cafes in your area'}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredShops.map((shop) => (
              <Link key={shop.id} to={`/shop/${shop.id}`} data-testid={`directory-shop-card-${shop.id}`}>
                <Card className="card-hover overflow-hidden border border-border bg-card shadow-sm">
                  <div className="overflow-hidden aspect-video">
                    <img
                      src={shop.image_url}
                      alt={shop.name}
                      className="w-full h-full object-cover image-zoom"
                    />
                  </div>
                  <CardContent className="p-6">
                    <h3 className="text-2xl font-medium mb-2 tracking-tight">
                      {shop.name}
                    </h3>
                    <div className="flex items-center gap-2 text-sm text-muted-foreground mb-3">
                      <MapPin className="h-4 w-4" strokeWidth={1.5} />
                      <span>{shop.address}</span>
                    </div>
                    <div className="flex items-center gap-1 mb-3">
                      <Star className="h-4 w-4 fill-accent text-accent" strokeWidth={1.5} />
                      <span className="text-sm font-medium">{shop.rating.toFixed(1)}</span>
                    </div>
                    <p className="text-sm text-muted-foreground line-clamp-2 mb-3">
                      {shop.description}
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {shop.specialties.slice(0, 3).map((specialty, idx) => (
                        <span
                          key={idx}
                          className="text-xs px-3 py-1 bg-secondary text-secondary-foreground rounded-full tracking-wide"
                        >
                          {specialty}
                        </span>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Directory;